import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import FormGroup from 'reactstrap/lib/FormGroup';
import Button from 'reactstrap/lib/Button';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { DEFAULT_PUB_STATES } from 'site-modules/shared/utils/make-model-zip';
import { PUB_STATES_NEW } from 'client/constants/pub-states';
import { StyledMakeDropdown } from 'site-modules/shared/components/vehicle-dropdowns/make-dropdown';
import { StyledSubmodelDropdown } from 'site-modules/shared/components/vehicle-dropdowns/submodel-dropdown';
import { StyledYearByMakeDropdown } from 'site-modules/shared/components/vehicle-dropdowns/year-by-make-dropdown';
import { StyledStyleDropdown } from 'site-modules/shared/components/vehicle-dropdowns/style-dropdown';
import { StyledZipInput } from 'site-modules/shared/components/styled-zip-input/styled-zip-input';
import { FieldError } from 'client/site-modules/shared/components/field-error/field-error';

export function VehicleSelector({ showLatestYears = false, generateBtnLink }) {
  const [make, setMake] = useState(null);
  const [model, setModel] = useState(null);
  const [year, setYear] = useState(null);
  const [style, setStyle] = useState(null);
  const [validated, setValidated] = useState(false);
  const [isZipChangeInProgress, setZipChangeInProgress] = useState(false);

  const btnLink = generateBtnLink({ make, model, year, style });

  const onMakeChange = useCallback(newMake => {
    setMake(newMake);
    setModel(null);
    setYear(null);
    setStyle(null);
    setValidated(false);
  }, []);

  const onModelChange = useCallback(newModel => {
    setModel(newModel && { name: newModel.name, slug: newModel.niceId, model: { slug: newModel.modelNiceId } });
    setStyle(null);
    setValidated(false);
  }, []);

  const onYearChange = useCallback(newYear => {
    setYear(newYear);
    setModel(null);
    setStyle(null);
    setValidated(false);
  }, []);

  const onStyleChange = useCallback(newStyle => {
    setStyle(newStyle);
    setValidated(false);
  }, []);

  const onZipChangeProgress = useCallback(({ isZipChangeInProgress: isInProgress }) => {
    setZipChangeInProgress(isInProgress);
  }, []);

  function validate() {
    setValidated(true);
  }

  return (
    <div className="vehicle-selector">
      <Row>
        <Col xs={12} md={6}>
          <StyledZipInput
            inputClasses="size-16"
            styledWrapperClasses="mb-1_5"
            onValidate={validation.validateZip}
            facetName="zip-input"
            onZipChangeInProgress={onZipChangeProgress}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={6}>
          <FormGroup className="mb-1_5 form-group" noMargin>
            <StyledMakeDropdown
              value={make && make.slug}
              onChange={onMakeChange}
              pubStates={showLatestYears ? PUB_STATES_NEW : DEFAULT_PUB_STATES}
              isLabelHidden
            />
            {validated && !make && (
              <FieldError id="makeError" classes="validation-error mb-0 mt-0_25" error="Please select a make" />
            )}
          </FormGroup>
        </Col>
        <Col xs={12} md={6}>
          <FormGroup className="mb-1_5 form-group" noMargin>
            <StyledYearByMakeDropdown
              make={make}
              value={year && year.year}
              onChange={onYearChange}
              isLabelHidden
              showLatestYears={showLatestYears}
              labelText="Year"
            />
            {validated && !year && (
              <FieldError id="yearError" classes="validation-error mb-0 mt-0_25" error="Please select a year" />
            )}
          </FormGroup>
        </Col>
        <Col xs={12} md={6}>
          <FormGroup className="mb-1_5 form-group" noMargin>
            <StyledSubmodelDropdown
              make={make}
              year={year && year.year}
              value={model && model.name}
              onChange={onModelChange}
              pubStates={DEFAULT_PUB_STATES}
              isLabelHidden
              labelText="Model"
              isMakeYearSubmodels
            />
            {validated && !model && (
              <FieldError id="modelError" classes="validation-error mb-0 mt-0_25" error="Please select a model" />
            )}
          </FormGroup>
        </Col>
        <Col xs={12} md={6}>
          <FormGroup className="mb-1_5 form-group" noMargin>
            <StyledStyleDropdown
              make={make}
              model={model && model.model}
              submodel={model}
              year={year}
              value={style && style.slug}
              onChange={onStyleChange}
              pubStates={DEFAULT_PUB_STATES}
              isLabelHidden
              labelText="Style"
            />
            {validated && !style && (
              <FieldError id="styleError" classes="validation-error mb-0 mt-0_25" error="Please select a style" />
            )}
          </FormGroup>
        </Col>
      </Row>
      <Button
        tag="a"
        href={btnLink}
        onClick={btnLink ? null : validate}
        disabled={isZipChangeInProgress}
        color="success"
        className="go-btn text-white text-capitalize"
      >
        Go
      </Button>
    </div>
  );
}

VehicleSelector.propTypes = {
  generateBtnLink: PropTypes.func.isRequired,
  showLatestYears: PropTypes.bool,
};
